import React from "react";
import {
  KBarPortal,
  KBarPositioner,
  KBarAnimator,
  KBarSearch,
  KBarResults,
  useMatches,
} from "@refinedev/kbar";
import type { ActionImpl, ActionId } from "kbar";
import { useTheme, alpha } from "@mui/material/styles";

const ThemedResultItem = React.forwardRef<
  HTMLDivElement,
  { action: ActionImpl; active: boolean; currentRootActionId: ActionId }
>(({ action, active, currentRootActionId }, ref) => {
  const theme = useTheme();

  const ancestors = React.useMemo(() => {
    if (!currentRootActionId) return action.ancestors;
    const index = action.ancestors.findIndex(
      (a) => a.id === currentRootActionId,
    );
    return action.ancestors.slice(index + 1);
  }, [action.ancestors, currentRootActionId]);

  return (
    <div
      ref={ref}
      style={{
        padding: "10px 16px",
        background: active
          ? alpha(theme.palette.primary.main, 0.1)
          : "transparent",
        borderLeft: `2px solid ${active ? theme.palette.primary.main : "transparent"}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        cursor: "pointer",
      }}
    >
      <div
        style={{
          display: "flex",
          gap: 8,
          alignItems: "center",
          fontSize: 14,
          color: theme.palette.text.primary,
        }}
      >
        {action.icon}
        <div style={{ display: "flex", flexDirection: "column" }}>
          <div>
            {ancestors.map((ancestor) => (
              <React.Fragment key={ancestor.id}>
                <span style={{ opacity: 0.5, marginRight: 8 }}>
                  {ancestor.name}
                </span>
                <span style={{ marginRight: 8 }}>&rsaquo;</span>
              </React.Fragment>
            ))}
            <span style={{ color: theme.palette.text.primary }}>
              {action.name}
            </span>
          </div>
          {action.subtitle && (
            <span style={{ fontSize: 12, color: theme.palette.text.secondary }}>
              {action.subtitle}
            </span>
          )}
        </div>
      </div>
      {action.shortcut?.length ? (
        <div
          aria-hidden
          style={{ display: "grid", gridAutoFlow: "column", gap: 4 }}
        >
          {action.shortcut.map((sc) => (
            <kbd
              key={sc}
              style={{
                padding: "4px 6px",
                background: alpha(theme.palette.text.primary, 0.1),
                color: theme.palette.text.primary,
                borderRadius: 4,
                fontSize: 12,
              }}
            >
              {sc}
            </kbd>
          ))}
        </div>
      ) : null}
    </div>
  );
});
ThemedResultItem.displayName = "ThemedResultItem";

const KbarSectionHeader = ({ label }: { label: string }) => {
  const theme = useTheme();
  return (
    <div
      style={{
        padding: "8px 16px",
        fontSize: 11,
        textTransform: "uppercase",
        fontWeight: 700,
        letterSpacing: "0.08em",
        color: theme.palette.text.secondary,
      }}
    >
      {label}
    </div>
  );
};

const ThemedRenderResults = () => {
  const { results, rootActionId } = useMatches();

  return (
    <KBarResults
      items={results}
      onRender={({ item, active }) =>
        typeof item === "string" ? (
          <KbarSectionHeader label={item} />
        ) : (
          <ThemedResultItem
            action={item}
            active={active}
            currentRootActionId={rootActionId as ActionId}
          />
        )
      }
    />
  );
};

export const ThemedKbar = () => {
  const theme = useTheme();

  return (
    <KBarPortal>
      <KBarPositioner
        style={{
          zIndex: 9999,
          backdropFilter: "saturate(180%) blur(2px)",
          background: alpha(theme.palette.common.black, 0.4),
        }}
      >
        <KBarAnimator
          style={{
            maxWidth: 620,
            width: "100%",
            background: theme.palette.background.paper,
            borderRadius: 10,
            overflow: "hidden",
            boxShadow: theme.shadows[24],
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <KBarSearch
            style={{
              padding: "14px 16px",
              fontSize: 16,
              width: "100%",
              boxSizing: "border-box",
              outline: "none",
              border: "none",
              borderBottom: `1px solid ${theme.palette.divider}`,
              background: theme.palette.background.paper,
              color: theme.palette.text.primary,
            }}
          />
          <ThemedRenderResults />
        </KBarAnimator>
      </KBarPositioner>
    </KBarPortal>
  );
};
